import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { startToolOperation, type ToolAnalyticsContext } from '@saas-pages/shared/public/runtime'
import {
  createCalendarMonth,
  createCalendarYear,
  enableIsoWeekNumbers,
  hasCalendarMonthViewIntent,
  selectWeekStart,
  type CalendarDay,
  type CalendarMonth,
  type WeekStart,
} from '@/pages/calendar/model/calendar'
import { localeCode, useLocale, useT, type Translations } from '@/shared/i18n'

const PRERENDER_YEAR = 2026

interface CalendarPageProps {
  initialYear?: number
}

type CalendarView = 'year' | 'month'

function calendarAnalyticsContext(initialYear: number | undefined): ToolAnalyticsContext {
  if (initialYear === 2026 || initialYear === 2027) {
    return {
      app: 'dateify',
      domain: 'dateify.ru',
      route: `/kalendar-${initialYear}`,
      utility: `calendar-${initialYear}`,
      processingMode: 'local',
    }
  }

  return {
    app: 'dateify',
    domain: 'dateify.ru',
    route: '/kalendar',
    utility: 'calendar',
    processingMode: 'local',
  }
}

export function CalendarPage({ initialYear }: CalendarPageProps) {
  const t = useT()
  const { locale } = useLocale()
  const [year, setYear] = useState(initialYear ?? PRERENDER_YEAR)
  const [month, setMonth] = useState(1)
  const [view, setView] = useState<CalendarView>('year')
  const [weekStart, setWeekStart] = useState<WeekStart>('monday')
  const [showIsoWeeks, setShowIsoWeeks] = useState(false)
  const analyticsContext = useMemo(
    () => calendarAnalyticsContext(initialYear),
    [initialYear],
  )

  useEffect(() => {
    if (initialYear !== undefined) return
    const now = new Date()
    setYear(now.getFullYear())
    setMonth(now.getMonth() + 1)
    if (hasCalendarMonthViewIntent(window.location.search)) setView('month')
  }, [initialYear])

  const calendarMonths = useMemo(
    () => view === 'year'
      ? createCalendarYear(year, weekStart)
      : [createCalendarMonth(year, month, weekStart)],
    [month, view, weekStart, year],
  )

  const handleYear = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextYear = Number(event.target.value)
    if (Number.isInteger(nextYear) && nextYear >= 1 && nextYear <= 9999) setYear(nextYear)
  }, [])

  const handleToday = useCallback(() => {
    if (initialYear !== undefined) {
      startToolOperation(analyticsContext).success(0)
      window.location.assign('/kalendar?view=month')
      return
    }
    const now = new Date()
    setYear(now.getFullYear())
    setMonth(now.getMonth() + 1)
    setView('month')
    startToolOperation(analyticsContext).success(0)
  }, [analyticsContext, initialYear])

  const handleReset = useCallback(() => {
    setYear(initialYear ?? new Date().getFullYear())
    setMonth(1)
    setView('year')
    setWeekStart('monday')
    setShowIsoWeeks(false)
    startToolOperation(analyticsContext).success(0)
  }, [analyticsContext, initialYear])

  const handlePrint = useCallback(() => {
    const startedAt = performance.now()
    startToolOperation(analyticsContext).success(Math.round(performance.now() - startedAt))
    window.print()
  }, [analyticsContext, view])

  const handleWeekStart = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const options = selectWeekStart(event.target.value as WeekStart, showIsoWeeks)
    setWeekStart(options.weekStart)
    setShowIsoWeeks(options.showIsoWeeks)
  }, [showIsoWeeks])

  const handleIsoWeeks = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const options = enableIsoWeekNumbers(event.target.checked, weekStart)
    setWeekStart(options.weekStart)
    setShowIsoWeeks(options.showIsoWeeks)
  }, [weekStart])

  const title = initialYear
    ? t.calendar.yearTitle.replace('{year}', String(initialYear))
    : t.calendar.title
  const description = initialYear
    ? t.calendar.yearDescription.replace('{year}', String(initialYear))
    : t.calendar.description
  const faq = initialYear
    ? t.calendar.yearFaq.map((item) => ({
      question: item.question.replace('{year}', String(initialYear)),
      answer: item.answer.replace('{year}', String(initialYear)),
    }))
    : t.calendar.faq
  const monthNames = useMemo(
    () => Array.from({ length: 12 }, (_, index) => new Intl.DateTimeFormat(
      localeCode(locale),
      { month: 'long', timeZone: 'UTC' },
    ).format(new Date(Date.UTC(2026, index, 1)))),
    [locale],
  )
  const printHeading = view === 'month'
    ? `${monthNames[month - 1] ?? ''} ${year}`
    : initialYear === undefined
      ? `${title} — ${year}`
      : title

  return (
    <div className="calendar-page mx-auto max-w-7xl">
      <section className="calendar-screen-only pb-4 pt-1 sm:pb-8 sm:pt-4">
        <div className="mb-2 inline-flex rounded-md border bg-card px-2 py-0.5 text-xs font-medium text-muted-foreground shadow-sm sm:mb-3 sm:px-2.5 sm:py-1">
          {t.common.localBadge}
        </div>
        <h1 className="text-2xl font-bold tracking-normal sm:text-4xl">{title}</h1>
        <p className="mt-1 line-clamp-2 max-w-3xl text-sm leading-6 text-muted-foreground sm:mt-2 sm:line-clamp-none sm:text-base sm:leading-7">
          {description}
        </p>
      </section>

      <Card className="calendar-controls calendar-screen-only mb-6 overflow-hidden">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-cyan-400 via-primary to-emerald-400" />
        <CardContent className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4 lg:p-6">
          <label className="block" htmlFor="calendar-year">
            <span className="mb-2 block text-sm font-medium">{t.calendar.year}</span>
            <input
              id="calendar-year"
              type="number"
              min={1}
              max={9999}
              value={year}
              onChange={handleYear}
              disabled={initialYear !== undefined}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </label>
          <label className="block" htmlFor="calendar-month">
            <span className="mb-2 block text-sm font-medium">{t.calendar.month}</span>
            <select
              id="calendar-month"
              value={month}
              onChange={(event) => setMonth(Number(event.target.value))}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            >
              {monthNames.map((name, index) => <option key={name} value={index + 1}>{name}</option>)}
            </select>
          </label>
          <fieldset>
            <legend className="mb-2 text-sm font-medium">{t.calendar.view}</legend>
            <div className="flex h-10 rounded-md border bg-background p-1">
              <SegmentButton selected={view === 'year'} onClick={() => setView('year')}>
                {t.calendar.yearView}
              </SegmentButton>
              <SegmentButton selected={view === 'month'} onClick={() => setView('month')}>
                {t.calendar.monthView}
              </SegmentButton>
            </div>
          </fieldset>
          <label className="block" htmlFor="calendar-week-start">
            <span className="mb-2 block text-sm font-medium">{t.calendar.weekStarts}</span>
            <select
              id="calendar-week-start"
              value={weekStart}
              onChange={handleWeekStart}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="monday">{t.calendar.monday}</option>
              <option value="sunday">{t.calendar.sunday}</option>
            </select>
          </label>

          <div className="flex flex-wrap items-center gap-3 sm:col-span-2 lg:col-span-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showIsoWeeks}
                onChange={handleIsoWeeks}
                className="h-4 w-4 rounded border-input accent-primary"
              />
              {t.calendar.showIsoWeeks}
            </label>
            <div className="ml-auto flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={handleToday}>{t.calendar.today}</Button>
              <Button type="button" variant="ghost" onClick={handleReset}>{t.calendar.reset}</Button>
              <Button type="button" onClick={handlePrint}>{t.calendar.print}</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="calendar-print-heading mb-5 hidden">
        <p className="text-2xl font-bold capitalize">{printHeading}</p>
      </div>

      <div className={cn(
        'calendar-grid grid gap-4',
        view === 'year' ? 'calendar-grid-year' : 'calendar-grid-month',
        view === 'year' && 'md:grid-cols-2 xl:grid-cols-3',
      )}>
        {calendarMonths.map((calendarMonth) => (
          <MonthCalendar
            key={calendarMonth.month}
            calendarMonth={calendarMonth}
            monthName={monthNames[calendarMonth.month - 1] ?? ''}
            showIsoWeeks={showIsoWeeks}
            weekStart={weekStart}
            locale={localeCode(locale)}
            t={t}
          />
        ))}
      </div>

      <aside className="calendar-disclaimer mt-6 rounded-lg border border-amber-400/40 bg-amber-100/50 p-4 text-sm leading-6 text-amber-950 dark:bg-amber-950/20 dark:text-amber-100">
        <p className="font-medium">{t.calendar.disclaimer}</p>
        {showIsoWeeks && <p className="mt-1 text-current/80">{t.calendar.isoNote}</p>}
      </aside>

      <section className="calendar-screen-only mt-10 border-t pt-8">
        <h2 className="text-2xl font-semibold tracking-normal">{t.calendar.faqTitle}</h2>
        <div className="mt-4 grid gap-3 lg:grid-cols-3">
          {faq.map((item) => (
            <article key={item.question} className="rounded-lg border bg-card p-5 shadow-sm">
              <h3 className="font-semibold">{item.question}</h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{item.answer}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}

function SegmentButton({
  selected,
  onClick,
  children,
}: {
  selected: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'flex-1 rounded px-2 text-xs font-medium transition-colors',
        selected ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}

function MonthCalendar({
  calendarMonth,
  monthName,
  showIsoWeeks,
  weekStart,
  locale,
  t,
}: {
  calendarMonth: CalendarMonth
  monthName: string
  showIsoWeeks: boolean
  weekStart: WeekStart
  locale: string
  t: Translations
}) {
  const weekdays = weekStart === 'monday'
    ? t.calendar.weekdaysMonday
    : t.calendar.weekdaysSunday

  return (
    <article className="calendar-month rounded-lg border bg-card p-3 shadow-sm sm:p-4">
      <table className="w-full table-fixed border-separate border-spacing-1 text-center">
        <caption className="mb-3 text-lg font-semibold capitalize tracking-normal">
          {monthName} {calendarMonth.year}
        </caption>
        <thead>
          <tr>
            {showIsoWeeks && (
              <th scope="col" className="py-1 text-[10px] font-medium text-muted-foreground">
                {t.calendar.isoWeekShort}
              </th>
            )}
            {weekdays.map((weekday, index) => (
              <th
                key={weekday}
                scope="col"
                className={cn(
                  'py-1 text-xs font-medium text-muted-foreground',
                  (weekStart === 'monday' ? index >= 5 : index === 0 || index === 6) && 'text-primary',
                )}
              >
                {weekday}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {calendarMonth.weeks.map((week, rowIndex) => (
            <tr key={`${calendarMonth.month}-${rowIndex}`}>
              {showIsoWeeks && (
                <th scope="row" className="h-8 rounded bg-muted/50 text-[10px] font-normal text-muted-foreground">
                  <span className="sr-only">{t.calendar.isoWeekShort} </span>
                  {week.isoWeek}
                </th>
              )}
              {week.days.map((day, index) => day ? (
                <td
                  key={day.day}
                  className={cn(
                    'h-8 rounded text-sm tabular-nums',
                    day.isWeekend && 'bg-primary/10 font-medium text-primary dark:bg-primary/15',
                  )}
                >
                  <time dateTime={calendarDateTime(day)} aria-label={calendarDayLabel(day, locale)}>
                    {day.day}
                  </time>
                </td>
              ) : (
                <td key={`empty-${index}`} className="h-8" />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </article>
  )
}

function calendarDateTime(day: CalendarDay): string {
  return [
    String(day.year).padStart(4, '0'),
    String(day.month).padStart(2, '0'),
    String(day.day).padStart(2, '0'),
  ].join('-')
}

function calendarDayLabel(day: CalendarDay, locale: string): string {
  const date = new Date(0)
  date.setUTCHours(0, 0, 0, 0)
  date.setUTCFullYear(day.year, day.month - 1, day.day)
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'full',
    timeZone: 'UTC',
  }).format(date)
}
